import angular from 'angular';
import moment from 'moment';

/**
 * @ngdoc function
 * @name myConsultApp.controller:table
 * @description
 * # table
 * Controller of the myConsultApp
 */
angular
  .module('myConsultApp')
  .controller('storyTableController', ($http, ENV, $ngRedux, $scope, $rootScope, $timeout) => {
    $scope.$on('$destroy', () => {});

    $scope.isLoading = false;
    $scope.isFilterValid = true;
    $scope.showPageOptions = false;
    $scope.showSortOptions = false;
    $scope.records = [];
    $scope.currentPage = 1;
    $scope.totalCount = 0;
    $scope.totalPages = 1;
    $scope.pagesToDisplay = [];
    $scope.pageSizeOptions = [15, 25, 50, 100, 200];
    $scope.pageSize = 15;
    $scope.filters = [];
    $scope.sortOptions = {};

    $scope.apiUrl = '';

    $scope.init = () => {
      $scope.pageSize = $scope.storyTable.options.pageSize;
      $scope.pageSizeOptions = $scope.storyTable.options.pageSizeOptions;
      $scope.filters = $scope.storyTable.filters;
      $scope.sortOptions = $scope.storyTable.sortOptions;

      $scope.apiUrl = `${ENV.apiEndpoint}${$scope.storyTable.options.endpoint}`;

      $scope.getStories();

      $scope.watchChanges();
    };

    $scope.getParameters = (defaultParameters) => {
      const parameters = {
        ...defaultParameters,
        orderby: 'submission_date desc',
        count: true,
        page_size: $scope.pageSize,
      };

      if ($scope.currentPage > 1) {
        parameters.page = $scope.currentPage;
      }

      const filterParams = [];

      // if not production environment add filter to only return test data
      if (ENV.name !== 'production') {
        filterParams.push(`is_test eq 'True'`);
      } else {
        filterParams.push(`is_test eq 'False'`);
      }

      angular.forEach($scope.filters, (filter) => {
        if (filter.type === 'date_range') {
          const { from, to } = filter.options;

          if (from.value) {
            filterParams.push(
              `${filter.key} ${from.queryString} ${moment(from.value).format('YYYY-MM-DD')}`,
            );
          }

          if (to.value) {
            filterParams.push(
              `${filter.key} ${to.queryString} ${moment(to.value).format('YYYY-MM-DD')}`,
            );
          }
          parameters.submission_date = `${moment(from.value).format('YYYY-MM-DD')}:${moment(
            to.value,
          ).format('YYYY-MM-DD')}`;
        }

        if (filter.type === 'dropdown' && filter.options.selected.value) {
          filterParams.push(`${filter.key} eq '${filter.options.selected.value}'`);
          parameters[filter.key] = filter.options.selected.value;
        }
      });

      if (filterParams.length > 0) {
        parameters.filter = filterParams.join(' and ');
      }

      const sortOptions = [];

      Object.entries($scope.sortOptions).forEach(([key, item]) => {
        if (item.state === true) {
          sortOptions.push(item.value === '1' ? `${key} asc` : `${key} desc`);
          parameters.order = item.value === '1' ? `${key}` : `-${key}`;
        }
      });

      if (sortOptions.length > 0) {
        parameters.orderby = sortOptions.join(' and ');
      }

      return parameters;
    };

    $scope.getStories = () => {
      if ($scope.pageSize !== $scope.storyTable.options.pageSize) {
        $scope.storyTable.options.pageSize = $scope.pageSize;
        $scope.currentPage = 1;
      }

      const params = $scope.getParameters();

      $scope.isLoading = true;

      $http
        .get($scope.apiUrl, { params: params }, { withCredentials: false })
        .then(({ data }) => {
          $scope.records = data.value;
          angular.forEach($scope.records, (record) => {
            record.subscriberKey = record.subscriberKey.replace('qa-', '');
          });
          $scope.totalCount = data['@odata.count'];
          $scope.totalPages = Math.ceil($scope.totalCount / $scope.pageSize);

          $scope.generatePages();
        })
        .finally(() => {
          $scope.isLoading = false;
        });
    };

    $scope.watchChanges = () => {
      angular.forEach($scope.filters, async (filter, filterIndex) => {
        if (filter.type === 'date_range') {
          const { options } = filter;

          const watchHandler = async () => {
            const [from, to] = await $scope.formatDateRange(options.from.value, options.to.value);

            options.from.value = from;
            options.to.value = to;

            $scope.validateFilters();
          };

          $scope.$watch(`filters[${filterIndex}].options.from.value`, watchHandler);
          $scope.$watch(`filters[${filterIndex}].options.to.value`, watchHandler);
        }
      });
    };

    $scope.goToPage = (page) => {
      if ($scope.currentPage === page || $scope.isLoading) {
        return;
      }

      $scope.currentPage = page;
      $scope.getStories();
    };

    $scope.nextPage = () => {
      if ($scope.currentPage >= $scope.totalPages || $scope.isLoading) {
        return;
      }

      $scope.currentPage += 1;
      $scope.getStories();
    };

    $scope.prevPage = () => {
      if ($scope.currentPage <= 1 || $scope.isLoading) {
        return;
      }

      $scope.currentPage -= 1;
      $scope.getStories();
    };

    $scope.validatePageNumberInput = () => {
      if ($scope.pageNumberInput < 0) {
        $scope.pageNumberInput = 1;
      }
      if ($scope.pageNumberInput > $scope.totalPages) {
        $scope.pageNumberInput = $scope.totalPages;
        $scope.pageNumberInputError = `Max page = ${$scope.totalPages}`;
      } else {
        $scope.pageNumberInputError = '';
      }
    };

    $scope.generatePages = () => {
      let numbers = [];

      if ($scope.totalPages === 1) {
        numbers = [];
      } else if ($scope.totalPages < 7) {
        numbers = Array($scope.totalPages)
          .fill(0)
          .map((_, idx) => idx + 1);
      } else if ($scope.currentPage >= 1 && $scope.currentPage <= 3) {
        numbers = [1, 2, 3, 4, '...', $scope.totalPages];
      } else if ($scope.currentPage >= $scope.totalPages - 3) {
        numbers = [
          1,
          '...',
          $scope.totalPages - 3,
          $scope.totalPages - 2,
          $scope.totalPages - 1,
          $scope.totalPages,
        ];
      } else {
        numbers = [
          1,
          '...',
          $scope.currentPage - 1,
          $scope.currentPage,
          $scope.currentPage + 1,
          '...',
          $scope.totalPages,
        ];
      }

      $scope.pagesToDisplay = numbers;
    };

    $scope.downloadData = () => {
      const csvHeaders = Object.keys($scope.records[0]);
      const csvString = [csvHeaders, ...$scope.records.map((story) => Object.values(story))]
        .map((e) => e.join(','))
        .join('\n');

      const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8' });

      const url = window.URL || window.webkitURL;
      const link = url.createObjectURL(blob);
      const a = document.createElement('a');
      a.download = 'stories.csv';
      a.href = link;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    };

    $scope.toggleShowPageOptions = () => {
      $scope.showPageOptions = !$scope.showPageOptions;
    };

    $scope.toggleShowSortOptions = () => {
      $scope.showSortOptions = !$scope.showSortOptions;
    };

    $scope.selectPageSize = (pageSize) => {
      $scope.pageSize = pageSize;
    };

    $scope.selectSort = async (option_key, value) => {
      await angular.forEach($scope.sortOptions, async (_, key) => {
        if (key === option_key) {
          $scope.sortOptions[key].value = value;
          $scope.sortOptions[key].state = true;
        }
      });
    };

    $scope.toggleDropdown = (filterIndex) => {
      if (
        !$scope.filters[filterIndex].options.disabled &&
        $scope.filters[filterIndex].options.choices.length > 0
      ) {
        $scope.filters[filterIndex].options.show = !$scope.filters[filterIndex].options.show;
      }
    };

    $scope.selectDropdownOption = (filterIndex, choice) => {
      $scope.filters[filterIndex].options.selected = choice;
    };

    $scope.sortTitles = () =>
      Object.values($scope.sortOptions)
        .filter((item) => !!item.state)
        .map((item) => item.title)
        .join(', ');

    $scope.goToStoryPage = () => {};

    $scope.scrollToTop = () => {
      $('story-table .container').scrollTop(0);
      $('story-table .filter-panel').scrollTop(0);
    };

    $scope.formatDateRange = (fromStr, toStr) => {
      let from;
      let to;

      if (fromStr) {
        const date = moment(fromStr);
        from = date.isValid() ? date.format('YYYY-MM-DD') : fromStr;
      }
      if (toStr) {
        const date = moment(toStr);
        to = date.isValid() ? date.format('YYYY-MM-DD') : toStr;
      }
      return [from, to];
    };

    $scope.validateFilters = () => {
      let isFilterValid = true;

      // eslint-disable-next-line no-restricted-syntax
      for (let filter of $scope.filters) {
        const { type, options } = filter;

        if (type === 'date_range') {
          const { from, to } = options;
          from.error = false;
          to.error = false;

          if (from.required && !from.value) {
            from.error = true;
          }

          if (to.required && !to.value) {
            to.error = true;
          }

          if (from.value && to.value) {
            const fromMoment = moment(from.value).valueOf();
            const toMoment = moment(to.value).valueOf();

            if (toMoment - fromMoment < 0) {
              from.error = true;
              to.error = true;
            }
          }

          if (from.error || to.error) {
            isFilterValid = false;
            break;
          }
        }
      }

      $scope.isFilterValid = isFilterValid;
    };

    $scope.init();

    $(document).on('click', (event) => {
      $scope.clickOutside(event, 'story-table .page-content', $scope.showPageOptions, () => {
        $scope.showPageOptions = false;
      });

      $scope.clickOutside(event, 'story-table .sort-content', $scope.showSortOptions, () => {
        $scope.showSortOptions = false;
      });

      $scope.filters.forEach((filter, filterIndex) => {
        if (filter.type === 'dropdown') {
          $scope.clickOutside(
            event,
            `story-table .dropdown-${filter.key}`,
            filter.options.show,
            () => {
              $scope.filters[filterIndex].options.show = false;
            },
          );
        }
      });
    });

    $scope.clickOutside = (event, selector, currentState, callback) => {
      const element = $(selector);
      const $target = $(event.target);

      if (
        element !== event.target &&
        !$target.closest(selector).length &&
        !element.has(event.target).length &&
        currentState
      ) {
        $timeout(callback);
      }
    };
  })
  .directive('storyTable', () => {
    return {
      templateUrl: 'templates/v2/story-table.html',
    };
  });
